import * as THREE from "three";
import debug from "debug";

const log = debug("app:txt");


/**
 * A modal class for managing text box editing options in a 3D experience.
 * @category Components
 * @subcategory TextBox
 */
export class TextBoxModal {
  static instance = null;

  /**
   * Creates a new instance or returns the existing singleton instance of TextBoxModal.
   * @param {Object} experience - The 3D experience containing the scene and event hub.
   */

  constructor(experience) {
    if (TextBoxModal.instance) return TextBoxModal.instance;

    this.experience = experience;
    this.eventHub = this.experience.eventHub;
    this.toolbarContainer = null;
    this.currentTextBox = null;
    this.dragging = false;
    this.dragOffset = { x: 0, y: 0 };
    this.initModal();
    this.setEventListeners();

    TextBoxModal.instance = this;
  }

  /**
   * Retrieves the singleton instance of TextBoxModal.
   * @param {Object} experience - The 3D experience containing the scene and event hub.
   * @returns {TextBoxModal} The singleton instance.
   */

  static getInstance(experience) {
    if (!TextBoxModal.instance) {
      new TextBoxModal(experience);
    }
    return TextBoxModal.instance;
  }
  /**
   * Sets up event listeners for text box editing events.
   */
  setEventListeners() {
    this.eventHub.on("editTextBox", (cssGroup, scale) => {
      this.onEditTextBox(cssGroup, scale);
    });

    this.eventHub.on("exitEditTextBox", () => {
      this.hideModal();
      this.currentTextBox = null;
    });
  }

  cleanup() {
    console.log("TextBoxModal cleanup started...");
  
    // 1. Remove event listeners from eventHub
    if (this.eventHub) {
      this.eventHub.off("editTextBox", this.onEditTextBox);
      this.eventHub.off("exitEditTextBox", this.hideModal);
    }
  
    // 2. Remove toolbar event listeners
    const removeListener = (id, event, handler) => {
      const element = document.getElementById(id);
      if (element) {
        element.removeEventListener(event, handler);
      }
    };
  
    removeListener("toggleBold", "click", this.toggleBoldHandler);
    removeListener("textColor", "input", this.textColorHandler);
    removeListener("bgColor", "input", this.bgColorHandler);
    removeListener("outlineColor", "input", this.outlineColorHandler);
    removeListener("fontSize", "input", this.fontSizeHandler);
    removeListener("fontFamily", "change", this.fontFamilyHandler);
    removeListener("closeToolbar", "click", this.closeToolbarHandler);
  
    // 3. Remove drag event listeners
    document.removeEventListener("mousemove", this.dragMoveHandler);
    document.removeEventListener("mouseup", this.dragEndHandler);
  
    // 4. Remove toolbar from DOM
    if (this.toolbarContainer) {
      this.toolbarContainer.remove();
      this.toolbarContainer = null;
    }
  
    // 5. Reset stored references
    this.currentTextBox = null;
    this.dragging = false;
    this.dragOffset = { x: 0, y: 0 };
  
    console.log("TextBoxModal cleanup completed.");
  }

  /**
   * Handles the "editTextBox" event to enable editing for a specific text box.
   * @param {THREE.Group} cssGroup - The group containing the text box to edit.
   * @param {number} scale - The scale factor of the text box.
   */
  onEditTextBox(cssGroup, scale) {
    log("onEditTextBox Modal", cssGroup, scale)
    this.currentTextBox = cssGroup;
    this.dragging = false;
    this.updateModalValues(cssGroup);
    this.positionModal(cssGroup, scale);
    this.showModal();
    this.scale = scale;
  }

  /**
   * Initializes the modal toolbar for text box editing.
   */
  initModal() {
    // Create toolbar container with the original design
    this.toolbarContainer = document.createElement("div");
    this.toolbarContainer.style.position = "absolute";
    this.toolbarContainer.style.padding = "5px";
    this.toolbarContainer.style.border = "1px solid #ccc";
    this.toolbarContainer.style.borderRadius = "8px";
    this.toolbarContainer.style.backgroundColor = "#f9f9f9";
    this.toolbarContainer.style.boxShadow = "0 4px 8px rgba(0, 0, 0, 0.1)";
    this.toolbarContainer.style.zIndex = "11002";
    this.toolbarContainer.style.display = "none"; // Initially hidden
    this.toolbarContainer.style.alignItems = "center";
    this.toolbarContainer.style.justifyContent = "space-between";
    this.toolbarContainer.style.cursor = "move"; // Enable drag cursor

    // Updated configuration object for toolbar styles
    const toolbarConfig = {
      button: {
        width: "30px",
        height: "30px",
        margin: "0 5px",
      },
      colorPicker: {
        width: "40px",
        height: "30px",
        margin: "0 2px",
      },
      fontSize: {
        width: "50px", // Increased width for easier input
        height: "30px",
        margin: "0 5px",
      },
      fontFamily: {
        width: "150px", // Increased width to fit font names
        height: "30px",
        margin: "0 5px",
      },
      closeButton: {
        width: "30px",
        height: "30px",
        backgroundColor: "#ff4b4b",
        color: "white",
        border: "none",
        borderRadius: "4px",
        margin: "0 5px",
      },
      gap: "3px", // Space between elements
    };

    // Apply the existing CSS classes to the toolbar elements
    this.toolbarContainer.innerHTML = `
  <div style="display: flex; align-items: center; gap: ${toolbarConfig.gap}; pointer-events: auto;">
    <button type="button" id="toggleBold" title="Bold"
      style="width: ${toolbarConfig.button.width}; height: ${toolbarConfig.button.height}; display: flex; justify-content: center; align-items: center;">
      <b>B</b>
    </button>
    <input type="color" id="textColor" title="Text Color" class="textbox-modal-color"
      style="width: ${toolbarConfig.colorPicker.width}; height: ${toolbarConfig.colorPicker.height}; margin: ${toolbarConfig.colorPicker.margin};" 
      title="Set the text color of the text box."> <!-- Tooltip added -->
    <input type="color" id="bgColor" title="Background Color" class="textbox-modal-color"
      style="width: ${toolbarConfig.colorPicker.width}; height: ${toolbarConfig.colorPicker.height}; margin: ${toolbarConfig.colorPicker.margin};" 
      title="Set the background color of the text box."> <!-- Tooltip added -->
    <input type="number" id="fontSize" title="Font Size"
      min="1" max="100" value="14" placeholder="14"
      style="width: ${toolbarConfig.fontSize.width}; height: ${toolbarConfig.fontSize.height}; text-align: center; pointer-events: auto;">
    <select id="fontFamily" title="Font Family"
      style="width: ${toolbarConfig.fontFamily.width}; height: ${toolbarConfig.fontFamily.height}; pointer-events: auto;">
      <option value="Arial">Arial</option>
      <option value="Courier New">Courier New</option>
      <option value="Georgia">Georgia</option>
      <option value="Times New Roman">Times New Roman</option>
      <option value="Verdana">Verdana</option>
      <option value="Roboto">Roboto</option>
      <option value="Open Sans">Open Sans</option>
    </select>
    <input type="color" id="outlineColor" title="Border Color" class="textbox-modal-color"
      style="width: ${toolbarConfig.colorPicker.width}; height: ${toolbarConfig.colorPicker.height}; margin: ${toolbarConfig.colorPicker.margin};" 
      title="Set the border color of the text box."> 
    <button type="button" id="closeToolbar" title="Close"
      style="width: ${toolbarConfig.closeButton.width}; height: ${toolbarConfig.closeButton.height}; display: flex; justify-content: center; align-items: center; background-color: ${toolbarConfig.closeButton.backgroundColor}; color: ${toolbarConfig.closeButton.color}; border: ${toolbarConfig.closeButton.border}; border-radius: ${toolbarConfig.closeButton.borderRadius}; pointer-events: auto;">
      X
    </button>
  </div>
`;

    // Apply the CSS class for the main container
    this.toolbarContainer.classList.add("textbox-modal-toolbar-container");

    // Append to the canvas element
    document.body.appendChild(this.toolbarContainer);
    log("Toolbar created and appended", this.toolbarContainer);

    this.addToolbarEventListeners();
    this.addDragEventListeners(); // Add drag event listeners
  }

  /**
   * Updates the modal toolbar values with the properties of the current text box.
   * @param {THREE.Group} cssGroup - The group containing the text box.
   */
  updateModalValues(cssGroup) {
    log("updatemodalvalues", cssGroup, cssGroup.userData.outlineColor);
    if (!this.currentTextBox || !this.toolbarContainer) return;
    const userData = cssGroup.userData;

    document.getElementById("textColor").value = this.convertColorToHex(
      userData.textColor
    );
    document.getElementById("bgColor").value = this.convertColorToHex(
      userData.bgColor
    );
    document.getElementById("outlineColor").value = this.convertColorToHex(
      userData.outlineColor
    );
    document.getElementById("fontSize").value = userData.fontSize;
    document.getElementById("fontFamily").value =
      userData.fontFamily || "Arial";
  }

  /**
   * Displays the modal toolbar.
   */
  showModal() {
    this.toolbarContainer.style.display = "flex";
  }

  /**
   * Hides the modal toolbar.
   */
  hideModal() {
    this.toolbarContainer.style.display = "none";
  }

  /**
   * Positions the modal toolbar near the text box in the 3D scene.
   * @param {THREE.Group} cssGroup - The group containing the text box.
   * @param {number} scale - The scale factor of the text box.
   */
  positionModal(cssGroup, scale) {
    log("modal data", cssGroup, scale);
    if (!this.currentTextBox || !this.currentTextBox.getWorldPosition) {
      log("Error: Invalid text box reference.");
      return;
    }

    // Re-fetch the camera instance to ensure it's up-to-date
    this.camera = this.experience.camera?.instance;

    // Ensure camera is a valid THREE.Camera instance
    if (!this.camera || !(this.camera instanceof THREE.Camera)) {
      log("Error: Invalid camera reference or camera not initialized.");
      return;
    }

    // Calculate screen position from 3D object position
    const objectPosition = new THREE.Vector3();
    this.currentTextBox.getWorldPosition(objectPosition);

    const screenPosition = objectPosition.project(this.camera);

    const canvasRect = this.experience.canvas.getBoundingClientRect();
    const left =
      (screenPosition.x * 0.5 + 0.5) * canvasRect.width + canvasRect.left;
    const top =
      (-screenPosition.y * 0.5 + 0.5) * canvasRect.height + canvasRect.top;
    // Reset drag state to ensure modal is always positioned correctly
    this.dragging = false;

    // Manually set toolbar height based on known value
    const toolbarHeight = 60; // For example, if you know the height is 40px

    // Check if userData has valid width and height
    const textBoxWidth = cssGroup?.userData?.w
      ? cssGroup.userData.w * scale * this.camera.zoom
      : 0;
    const textBoxHeight = cssGroup?.userData?.h
      ? cssGroup.userData.h * scale * this.camera.zoom
      : 0;

    // Calculate offsets
    const horizontalOffset = -(textBoxWidth / 2); // Move left by half the text box width
    const verticalOffset = -(textBoxHeight / 2 + toolbarHeight); // Move up by half the text box height plus toolbar height

    // Position it above the text box
    this.toolbarContainer.style.left = `${left + horizontalOffset}px`;
    this.toolbarContainer.style.top = `${top + verticalOffset}px`;
  }

  /**
   * Adds event listeners for toolbar controls.
   */
  addToolbarEventListeners() {
    document.getElementById("toggleBold").addEventListener("click", () => {
      if (!this.currentTextBox) return;
      this.currentTextBox.userData.bold = !this.currentTextBox.userData.bold;
      this.updateTextBox();
    });

    document.getElementById("textColor").addEventListener("input", (e) => {
      if (!this.currentTextBox) return;
      this.currentTextBox.userData.textColor = this.convertHexToColor(
        e.target.value
      );
      this.updateTextBox();
    });

    document.getElementById("bgColor").addEventListener("input", (e) => {
      if (!this.currentTextBox) return;
      this.currentTextBox.userData.bgColor = this.convertHexToColor(
        e.target.value
      );
      this.updateTextBox();
    });

    document.getElementById("fontSize").addEventListener("input", (e) => {
      if (!this.currentTextBox) return;
      this.currentTextBox.userData.fontSize = parseInt(e.target.value);
      this.updateTextBox();
    });

    document.getElementById("fontFamily").addEventListener("change", (e) => {
      if (!this.currentTextBox) return;
      this.currentTextBox.userData.fontFamily = e.target.value;
      this.updateTextBox();
    });

    // Update border color on input
    document.getElementById("outlineColor").addEventListener("input", (e) => {
      if (!this.currentTextBox) return;
      this.currentTextBox.userData.outlineColor = this.convertHexToColor(
        e.target.value
      );
      this.updateTextBox();
    });

    document.getElementById("closeToolbar").addEventListener("click", () => {
      this.hideModal();
      this.eventHub.emit("exitEditTextBox");
    });
  }

  updateTextBox() {
    this.eventHub.emit(
      "updateTextBox",
      this.currentTextBox.userData,
      this.scale
    );
  }

  addDragEventListeners = () => {
    this.toolbarContainer.addEventListener("mousedown", (event) => {
      // Check if the event target is an input, select, or button element
      if (["INPUT", "SELECT", "BUTTON"].includes(event.target.tagName)) {
        return; // Don't initiate dragging if interacting with a form element
      }
      event.preventDefault();
      this.dragging = true;
      this.dragOffset.x = event.clientX - this.toolbarContainer.offsetLeft;
      this.dragOffset.y = event.clientY - this.toolbarContainer.offsetTop;
      document.body.style.cursor = "move"; // Change cursor to move
    });

    document.addEventListener("mousemove", (event) => {
      if (this.dragging) {
        const left = event.clientX - this.dragOffset.x;
        const top = event.clientY - this.dragOffset.y;
        this.toolbarContainer.style.left = `${left}px`;
        this.toolbarContainer.style.top = `${top}px`;
      }
    });

    document.addEventListener("mouseup", () => {
      if (this.dragging) {
        this.dragging = false;
        document.body.style.cursor = "default"; // Reset cursor
      }
    });
  };

  /**
   * Converts a color object to a hex string.
   * @param {Object} color - The color object with red, green, and blue properties.
   * @returns {string} The hex color string.
   */
  convertColorToHex(color) {
    return `#${((1 << 24) | (color.red << 16) | (color.green << 8) | color.blue)
      .toString(16)
      .slice(1)}`;
  }

  /**
   * Converts a hex color string to a color object.
   * @param {string} hex - The hex color string.
   * @returns {Object} The color object.
   */
  convertHexToColor(hex) {
    const bigint = parseInt(hex.slice(1), 16);
    return {
      red: (bigint >> 16) & 255,
      green: (bigint >> 8) & 255,
      blue: bigint & 255,
    };
  }
}
